import { FC, useEffect, useState, useRef } from 'react';
import { Navigate, RouteProps } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useIdleTimer } from 'react-idle-timer';
import { Message, FullPageLoader } from 'learning-panel-components';
import { platform, PlatformAuthenticateResult, utils, UserRole } from 'network-utils';
import { translate } from 'translations';
import {
    selectLoading,
    selectIsPlatformAuthenticated,
    selectRole,
    setIsPlatformAuthenticated,
    setLoading,
    setUserId,
    setRole,
    setLocale,
    setUserUuid,
    setToken,
    setCustomerDisplayName,
    setIsLeanUser,
    setUserNickname,
    setCertificateLinkedinShareEnabled,
    setTenantName,
    setIsPLG,
    setUserFullName,
    setCustomBrandName,
    setIsCxUniversity,
    setContentManagement,
    setIsTournamentPackageEnabled,
    setLabsAccess
} from '@redux/slices/userSlice';
import { mixpanelIdentify, prepareGainsightData } from 'utils';

const PrivateRoute: FC<PrivateRouteProps> = (props: PrivateRouteProps) => {
    const { component: Component, permissionLevel, ...rest } = props;
    const dispatch = useDispatch();
    const [isLoadingPlatform, setIsLoadingPlatform] = useState<boolean>(true);
    const [hasLoginError, setHasLoginError] = useState<boolean>(false);
    const lastServerCall = useRef<Date>(new Date());
    const { auth: platformAuth, SERVER_CALL_INTERVAL_MS, ON_ACTION_THROTTLE_MS } = platform;

    const loading = useSelector(selectLoading);
    const isPlatformAuthenticated = useSelector(selectIsPlatformAuthenticated);
    const role = useSelector(selectRole);

    const handlePlatformError = (): void => {
        dispatch(setIsPlatformAuthenticated(false));
        setIsLoadingPlatform(false);
        setHasLoginError(true);
    };

    useEffect(() => {
        if (!isLoadingPlatform) dispatch(setLoading(false));
    }, [dispatch, isLoadingPlatform]);

    const onAction = async () => {
        if (new Date().getTime() - lastServerCall.current.getTime() > SERVER_CALL_INTERVAL_MS) {
            lastServerCall.current = new Date();
            const data = await platformAuth();
            if (data.errors) {
                handlePlatformError();
                return;
            }

            dispatch(setToken(data.token));
        }
    };

    useIdleTimer({
        onAction,
        throttle: ON_ACTION_THROTTLE_MS
    });

    const authenticatePlatform = () => {
        dispatch(setLoading(true));
        platformAuth()
            .then((data: PlatformAuthenticateResult) => {
                if (data.errors) handlePlatformError();
                else {
                    mixpanelIdentify({
                        id: data.email,
                        tenant: data.account_name,
                        role: data.role,
                        locale: data.locale,
                        isLean: data.is_lean_license
                    });
                    dispatch(setIsPlatformAuthenticated(true));
                    dispatch(setRole(utils.getRole(data)));
                    dispatch(setUserId(data.user_id));
                    dispatch(setUserUuid(data.user_uuid));
                    dispatch(setLocale(data.locale));
                    dispatch(setToken(data.token));
                    dispatch(setCustomerDisplayName(data.customer_display_name));
                    dispatch(setIsLeanUser(data.is_lean_license || false));
                    dispatch(setUserNickname(data.nickname));
                    dispatch(setUserFullName(data.full_name));
                    dispatch(setCertificateLinkedinShareEnabled(data.features?.certificate_linkedin_share));
                    dispatch(setTenantName(data.account_name));
                    dispatch(setIsPLG(data.is_plg_tenant));
                    dispatch(setIsCxUniversity(data.is_university));
                    dispatch(setCustomBrandName(data.custom_brand_name));
                    dispatch(setContentManagement(data.features?.content_management));
                    dispatch(setIsTournamentPackageEnabled(data.features?.tournaments_game_package_enabled));
                    dispatch(setLabsAccess(data.labs_access));
                    prepareGainsightData(data);
                    dispatch(setLoading(false));
                }
            })
            .catch(handlePlatformError);
    };

    useEffect(() => {
        authenticatePlatform();
    }, [setIsPlatformAuthenticated, setLoading, setUserId, setRole, setLocale, process.env.CB_USE_MIXPANEL]);

    const checkPermissions = (): boolean => {
        if (process.env.NODE_ENV !== 'development') {
            return permissionLevel ? permissionLevel.includes(role) : true;
        }
        return true;
    };

    if (loading) return <FullPageLoader />;

    if (!isPlatformAuthenticated) {
        if (!window.location.href.endsWith('/app/login')) {
            sessionStorage.setItem('cbHistory', window.location.href);
        }
        return <Navigate to={{ pathname: '/app/login' }} />;
    }

    if (!checkPermissions()) {
        return <Navigate to="/no_permission" />;
    }

    if (hasLoginError) {
        return <Message message={translate('NetworkError')} fullScreen />;
    }

    return <Component />;
};

interface PrivateRouteProps {
    component: FC;
    path: string;
    location?: any;
    render?: (props: RouteProps) => JSX.Element;
    permissionLevel?: Array<keyof typeof UserRole>;
}

export default PrivateRoute;
